import type { ConflictMatch } from "./match.js";
import {
  autopilotBot,
  createOnlineBots,
  type OnlineBotState,
  runOnlineBots,
} from "./online-room-bots.js";
import {
  advanceOnlineLifecycle,
  approvePauseChange,
  disconnectOnlineController,
  initialOnlineLifecycle,
  type OnlineLifecycleState,
  requestOnlineReclaim,
  requestPauseChange,
  validateOnlineLifecycle,
} from "./online-room-lifecycle.js";
import type { RecoveryCheckpoint } from "./recovery-types.js";
import type { ConflictConfig } from "./types.js";

interface HumanAssignment {
  readonly seatId: string;
  readonly sessionId: string;
}

export class OnlineAuthorityLifecycle {
  #state: OnlineLifecycleState;
  #bots: readonly OnlineBotState[];

  private constructor(state: OnlineLifecycleState, bots: readonly OnlineBotState[]) {
    this.#state = state;
    this.#bots = Object.freeze(structuredClone(bots));
  }

  static start(
    config: ConflictConfig,
    humans: readonly HumanAssignment[],
    match: ConflictMatch,
    resumed?: RecoveryCheckpoint,
  ): OnlineAuthorityLifecycle {
    const occupied = new Set(humans.map((entry) => entry.seatId));
    const generations = Object.fromEntries(
      humans.map((entry) => {
        const generation = match.controllerGeneration(entry.seatId) + (resumed ? 1 : 0);
        if (resumed) match.transitionController(entry.seatId, entry.sessionId, generation, "human");
        return [entry.seatId, generation];
      }),
    );
    const bots = createOnlineBots(config, occupied).map((bot) => {
      const retained = resumed?.bots.find((entry) => entry.seatId === bot.seatId);
      return retained
        ? structuredClone(retained)
        : {
            ...bot,
            sequence: match.nextControllerSequence(bot.seatId) - 1,
            nextDecisionAtMs: match.observeAs(bot.seatId).gameTimeMs,
          };
    });
    return new OnlineAuthorityLifecycle(
      initialOnlineLifecycle(
        config.seats.map((seat) => seat.id),
        humans,
        generations,
      ),
      bots,
    );
  }

  static recover(
    config: ConflictConfig,
    checkpoint: RecoveryCheckpoint,
    connectedSessions: readonly string[],
  ): OnlineAuthorityLifecycle {
    let state = checkpoint.lifecycle
      ? validateOnlineLifecycle(checkpoint.lifecycle)
      : initialOnlineLifecycle(
          config.seats.map((seat) => seat.id),
          checkpoint.roster.flatMap((entry) =>
            entry.seatId && entry.role !== "spectator"
              ? [{ seatId: entry.seatId, sessionId: entry.sessionId }]
              : [],
          ),
        );
    const connected = new Set(connectedSessions);
    for (const controller of state.controllers) {
      if (
        controller.mode === "human" &&
        controller.sessionId &&
        !connected.has(controller.sessionId)
      )
        state = disconnectOnlineController(state, controller.sessionId, checkpoint.gameTimeMs);
    }
    return new OnlineAuthorityLifecycle(state, checkpoint.bots);
  }

  disconnect(sessionId: string, gameTimeMs: number): void {
    this.#state = disconnectOnlineController(this.#state, sessionId, gameTimeMs);
  }

  reclaim(sessionId: string): void {
    this.#state = requestOnlineReclaim(this.#state, sessionId);
  }

  requestPause(sessionId: string, paused: boolean): void {
    this.#state = requestPauseChange(this.#state, sessionId, paused);
  }

  approvePause(sessionId: string): void {
    this.#state = approvePauseChange(this.#state, sessionId);
  }

  tick(
    match: ConflictMatch,
    config: ConflictConfig,
    elapsedMs: number,
    connectedHumans: readonly string[],
  ): void {
    const now = match.observeAs(config.seats[0]?.id ?? "").gameTimeMs;
    const advanced = advanceOnlineLifecycle(this.#state, now, connectedHumans);
    this.#state = advanced.state;
    for (const transition of advanced.transitions) {
      if (transition.kind === "pause-changed") match.recordPauseTransition(transition.paused);
      else {
        match.transitionController(
          transition.seatId,
          transition.sessionId,
          transition.generation,
          transition.mode,
        );
        this.updateBot(match, config, transition.seatId, transition.mode, now);
      }
    }
    if (this.#state.paused) return;
    this.#bots = runOnlineBots(match, config, this.#bots, now);
    match.tick(elapsedMs);
  }

  state(): OnlineLifecycleState {
    return this.#state;
  }

  bots(): readonly OnlineBotState[] {
    return this.#bots;
  }

  private updateBot(
    match: ConflictMatch,
    config: ConflictConfig,
    seatId: string,
    mode: "human" | "autopilot",
    gameTimeMs: number,
  ): void {
    if (mode === "human") {
      this.#bots = Object.freeze(this.#bots.filter((bot) => bot.seatId !== seatId));
      return;
    }
    if (this.#bots.some((bot) => bot.seatId === seatId)) return;
    this.#bots = Object.freeze([
      ...this.#bots,
      autopilotBot(seatId, config, gameTimeMs, match.nextControllerSequence(seatId) - 1),
    ]);
  }
}
